// Thin client for the billing-service HTTP API. Handles signed-request
// header injection on mutating endpoints; passes GET/public endpoints
// through unchanged.
//
// `next.config.js`'s rewrite proxies /billing/* to whichever backend
// the dashboard's NEXT_PUBLIC_BACKEND_BASE points at. Calling
// `fetch('/billing/account', ...)` from the browser hits the same
// origin (no CORS) — the rewrite layer relays to the backend.

import { signRequest } from './auth-signature';
import type { Identity } from './identity';

interface ApiError {
  error:   string;
  reason?: string;
  field?:  string;
}

export class ApiCallFailed extends Error {
  constructor(
    public readonly status: number,
    public readonly body:   ApiError | { error: string },
    message?: string,
  ) {
    super(message ?? `${status}: ${'error' in body ? body.error : 'request failed'}`);
    this.name = 'ApiCallFailed';
  }
}

async function call<T>(args: {
  method:    string;
  path:      string;       // begins with '/billing/...'
  body?:     unknown;       // serialized to JSON; '' if absent
  identity?: Identity;     // signs the request when provided
}): Promise<T> {
  const body_text = args.body === undefined ? '' : JSON.stringify(args.body);
  const headers: Record<string, string> = {};
  if (args.body !== undefined) headers['content-type'] = 'application/json';
  if (args.identity) {
    // Server signs `url.pathname` (no query string) — sign the same
    // here, otherwise endpoints that take query params (e.g.
    // /usage?window=…) get a 401 on signature mismatch.
    const path_for_sig = args.path.split('?', 1)[0]!;
    Object.assign(headers, signRequest({
      privkey:    args.identity.privkey,
      account_id: args.identity.account_id,
      method:     args.method,
      path:       path_for_sig,
      body:       body_text,
    }));
  }

  const res = await fetch(args.path, {
    method:  args.method,
    headers,
    body:    args.body === undefined ? undefined : body_text,
  });

  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { /* leave null */ }

  if (!res.ok) {
    throw new ApiCallFailed(res.status, (parsed as ApiError) ?? { error: text || 'request failed' });
  }
  return parsed as T;
}

// ============================================================
// Endpoints
// ============================================================

export interface AccountDto {
  account_id:               string;
  status:                   string;
  suspended_reason:         string | null;
  suspended_at:             string | null;
  tier:                     string;
  subscription_term:        string | null;
  tier_rate_per_cc:         number;
  balance_cc:               string;        // bigint as string
  cycle_started_at:         string | null;
  cycle_ends_at:            string | null;
  scheduled_downgrade_to:   string | null;
  scheduled_term_change:    string | null;
  cycle_discount_pct:       number;
  first_payment_at:         string | null;
  notify_prefs:             Record<string, unknown> | null;
  telegram_linked:          boolean;
  telegram_username:        string | null;
  created_at:               string;
  updated_at:               string;
}

export interface TokenDto {
  token_id:          string;
  account_id:        string;
  token_hash:        string;
  allowed_systems:   string[];
  allowed_networks:  string[];
  allowed_methods:   string[] | null;
  allowed_origins:   string[] | null;
  optional_caps:     Record<string, unknown> | null;
  /** Cumulative CC spent against this token (bigint as string). Only
   *  meaningful when optional_caps.spend_budget_cc is set. Hydrated by
   *  the list endpoint; '0' or undefined means no charged spend yet. */
  spent_cc?:         string;
  status:            string;
  expires_at:        string | null;
  created_at:        string;
  revoked_at:        string | null;
}

export interface IssuedToken extends TokenDto { raw_token: string }

export type UsageWindow = 'cycle' | '24h' | '7d' | '30d';

export interface UsageDto {
  window: UsageWindow;
  from:   string;             // ISO-8601
  to:     string;             // ISO-8601
  totals: {
    calls:      number;
    cc_charged: string;       // bigint as string
    executed:   number;
    cached:     number;
    rejected:   number;
    errored:    number;
  };
  top_methods: Array<{
    system:     string;
    network:    string;
    method:     string;
    calls:      number;
    cc_charged: string;
  }>;
  top_tokens: Array<{
    token_id:   string;
    calls:      number;
    cc_charged: string;
  }>;
}

/** POST /billing/account — first-touch. No signature; account_id IS the proof. */
export function createAccount(account_id: string): Promise<AccountDto> {
  return call<AccountDto>({ method: 'POST', path: '/billing/account', body: { account_id } });
}

/** GET /billing/account/:id — signed read, returns full AccountDto incl. notify_prefs. */
export function getAccount(identity: Identity): Promise<AccountDto> {
  return call<AccountDto>({
    method:   'GET',
    path:     `/billing/account/${identity.account_id}`,
    identity,
  });
}

/** GET /billing/account/:id/tokens — public list. */
export function listTokens(account_id: string): Promise<{ tokens: TokenDto[] }> {
  return call<{ tokens: TokenDto[] }>({
    method: 'GET',
    path:   `/billing/account/${account_id}/tokens`,
  });
}

/** GET /billing/account/:id/usage?window=…&token_id=… — signed read; usage_log aggregates. */
export function getUsage(
  identity: Identity,
  opts:    { window?: UsageWindow; token_id?: string } = {},
): Promise<UsageDto> {
  const params = new URLSearchParams();
  params.set('window', opts.window ?? 'cycle');
  if (opts.token_id) params.set('token_id', opts.token_id);
  return call<UsageDto>({
    method:   'GET',
    path:     `/billing/account/${identity.account_id}/usage?${params.toString()}`,
    identity,
  });
}

export interface ActivityRow {
  id:          string;
  ts:          string;          // ISO-8601
  token_id:    string;
  system:      string;
  network:     string;
  method:      string;
  cc_charged:  string;          // bigint as string
  outcome:     string;
  duration_ms: number;
  req_bytes:   number;
  resp_bytes:  number;
}

export interface ActivityPage {
  rows:        ActivityRow[];
  next_before: string | null;
}

export type OutcomeFilter = 'executed' | 'cached' | 'rejected' | 'error' | 'errors';

/** GET /billing/account/:id/activity — signed read; chronological usage_log stream. */
export function getActivity(
  identity: Identity,
  opts: { limit?: number; before?: string; token_id?: string; outcome?: OutcomeFilter } = {},
): Promise<ActivityPage> {
  const params = new URLSearchParams();
  if (opts.limit    !== undefined) params.set('limit',    String(opts.limit));
  if (opts.before   !== undefined) params.set('before',   opts.before);
  if (opts.token_id !== undefined) params.set('token_id', opts.token_id);
  if (opts.outcome  !== undefined) params.set('outcome',  opts.outcome);
  const qs = params.toString();
  return call<ActivityPage>({
    method:   'GET',
    path:     `/billing/account/${identity.account_id}/activity${qs ? `?${qs}` : ''}`,
    identity,
  });
}

/** POST /billing/token — signed. Returns the DTO + the one-time raw_token. */
export function issueToken(identity: Identity, body: {
  allowed_systems?:   string[];
  allowed_networks?:  string[];
  allowed_methods?:   string[];
  allowed_origins?:   string[];
  optional_caps?:     Record<string, unknown>;
  expires_at?:        string;
}): Promise<IssuedToken> {
  return call<IssuedToken>({
    method:   'POST',
    path:     '/billing/token',
    body:     body ?? {},
    identity,
  });
}

// ============================================================
// Payment requests
// ============================================================

export type PaymentPurpose = 'subscribe' | 'upgrade' | 'topup' | 'renewal';
export type PaymentMethod  = 'bch' | 'pusd' | 'musd';
export type SubscriptionTerm = 'monthly' | 'annual';
export type TierName = 'trial' | 'hobby' | 'build' | 'scale' | 'business' | 'dedicated';

export interface PaymentRequestDto {
  payment_request_id:        string;
  account_id:                string;
  purpose:                   string;
  target_tier:               string | null;
  target_term:               string | null;
  target_topup_usd:          number | null;
  amount_usd:                number;
  payment_method:            string;
  quote_amount_native:       string;
  fx_rate:                   number | null;
  fx_source:                 string | null;
  quote_at:                  string;
  /** Same script payload, encoded as a non-token cashaddr (q-prefix).
   *  Use for BCH payments — older BCH wallets reject the z-prefix. */
  deposit_address_cash:      string;
  /** Same script payload, encoded as a token-aware cashaddr (z-prefix).
   *  Required for CashToken (PUSD/MUSD) receivers. */
  deposit_address_token:     string;
  expires_at:                string;
  status:                    string;
  received_amount_native:    string;
  received_at:               string | null;
  last_partial_at:           string | null;
  applied_at:                string | null;
  created_at:                string;
  updated_at:                string;
}

/** Server-side no-op response when amount_usd == 0 (credit covers an upgrade). */
export interface PaymentNoOpDto { amount_usd: 0; message: string }

/** POST /billing/payment-request — public endpoint, no signature. The
 *  deposit address is per-request so there's no cross-account leakage,
 *  but anyone can quote a payment for any account_id; trade-off
 *  documented in PAYMENTS.md. */
export function createPayment(body: {
  account_id:        string;
  purpose:           PaymentPurpose;
  target_tier?:      TierName | null;
  target_term?:      SubscriptionTerm | null;
  target_topup_usd?: number | null;
  payment_method:    PaymentMethod;
}): Promise<PaymentRequestDto | PaymentNoOpDto> {
  return call<PaymentRequestDto | PaymentNoOpDto>({
    method: 'POST',
    path:   '/billing/payment-request',
    body,
  });
}

/** GET /billing/payment-request/:id — public read, polls status.
 *
 *  Wire shape: the server wraps the DTO as
 *  `{ payment_request: PaymentRequestDto, payouts: PayoutDto[] }`
 *  whereas POST /payment-request returns the flat DTO. Unwrap here
 *  so all three callers (dashboard DepositCard, /subscribe, and the
 *  /payment-request/[id] detail page) get the same shape they got
 *  from the original POST. (If a future caller wants `payouts`,
 *  expose a separate getPaymentWithPayouts helper.)
 */
export async function getPayment(payment_request_id: string): Promise<PaymentRequestDto> {
  const wrapped = await call<{ payment_request: PaymentRequestDto; payouts?: unknown[] }>({
    method: 'GET',
    path:   `/billing/payment-request/${payment_request_id}`,
  });
  return wrapped.payment_request;
}

/** POST /billing/payment-request/:id/abandon — customer-initiated cancel
 *  of a pending quote (no payment received). Signed; the signing
 *  account must own the quote. Idempotent on already-abandoned;
 *  rejects partial/paid/expired with 400. */
export function abandonPayment(identity: Identity, payment_request_id: string): Promise<PaymentRequestDto> {
  return call<PaymentRequestDto>({
    method: 'POST',
    path:   `/billing/payment-request/${payment_request_id}/abandon`,
    identity,
  });
}

/** GET /billing/account/:id/payment-requests — signed list of quotes. */
export function listPayments(
  identity: Identity,
  opts: { status?: 'open' | 'paid' | 'expired' | 'all'; limit?: number } = {},
): Promise<{ payments: PaymentRequestDto[] }> {
  const params = new URLSearchParams();
  if (opts.status !== undefined) params.set('status', opts.status);
  if (opts.limit  !== undefined) params.set('limit',  String(opts.limit));
  const qs = params.toString();
  return call<{ payments: PaymentRequestDto[] }>({
    method:   'GET',
    path:     `/billing/account/${identity.account_id}/payment-requests${qs ? `?${qs}` : ''}`,
    identity,
  });
}

/** POST /billing/token/:id/revoke — signed. Idempotent. */
export function revokeToken(identity: Identity, token_id: string): Promise<TokenDto> {
  return call<TokenDto>({
    method:   'POST',
    path:     `/billing/token/${token_id}/revoke`,
    identity,
  });
}

/** PATCH /billing/account/:id — signed. Updates notify_prefs (replace, not merge). */
export function updateNotifyPrefs(
  identity: Identity,
  notify_prefs: Record<string, unknown> | null,
): Promise<AccountDto> {
  return call<AccountDto>({
    method:   'PATCH',
    path:     `/billing/account/${identity.account_id}`,
    body:     { notify_prefs },
    identity,
  });
}

// ============================================================
// Telegram notifications
// ============================================================

export interface TelegramLinkDto {
  /** 16-hex one-time token used in the bot deep link. */
  token:      string;
  /** https://t.me/<bot>?start=<token> — open this in the customer's Telegram. */
  deep_link:  string;
  /** ISO-8601; the polling worker rejects /start past this. */
  expires_at: string;
}

/** POST /billing/account/:id/telegram/link — signed. Returns a fresh
 *  deep-link token. Dashboard renders the deep_link as a QR + "Open
 *  in Telegram" button, then polls /account every few seconds for
 *  telegram_linked === true. */
export function linkTelegram(identity: Identity): Promise<TelegramLinkDto> {
  return call<TelegramLinkDto>({
    method:   'POST',
    path:     `/billing/account/${identity.account_id}/telegram/link`,
    identity,
  });
}

/** POST /billing/account/:id/telegram/test — signed. Sends a test
 *  message to the linked chat. 400 if not linked, 503 if bot not
 *  configured on the rig. */
export function testTelegram(identity: Identity): Promise<{ sent: boolean }> {
  return call<{ sent: boolean }>({
    method:   'POST',
    path:     `/billing/account/${identity.account_id}/telegram/test`,
    identity,
  });
}

/** POST /billing/account/:id/telegram/disconnect — signed. Clears the
 *  chat_id binding. Idempotent (200 even if not linked). */
export function disconnectTelegram(identity: Identity): Promise<{ disconnected: boolean }> {
  return call<{ disconnected: boolean }>({
    method:   'POST',
    path:     `/billing/account/${identity.account_id}/telegram/disconnect`,
    identity,
  });
}

// ============================================================
// Web push
// ============================================================

export interface WebPushConfigDto {
  vapid_public_key: string;
  contact:          string;
}

/** GET /billing/web-push/config — public, unsigned. The dashboard
 *  fetches this to get the applicationServerKey for
 *  PushManager.subscribe(). 503 when the operator hasn't provisioned
 *  VAPID env vars; the dashboard renders a "not configured on this
 *  rig" hint in that case. */
export function getWebPushConfig(): Promise<WebPushConfigDto> {
  return call<WebPushConfigDto>({
    method: 'GET',
    path:   '/billing/web-push/config',
  });
}

// ============================================================
// Endpoint aliases (per-customer subdomains)
// ============================================================

export interface EndpointAliasDto {
  /** Server-minted random slug, ~64 bits entropy via 5 EFF-large
   *  wordlist words. The slug IS the secret; treat it like a
   *  password. */
  slug:          string;
  account_id:    string;
  token_id:      string;
  system:        string;
  network:       string;
  /** Customer-set human-friendly name shown in the dashboard alongside
   *  the slug — purely cosmetic, not the addressable identifier. */
  display_label: string | null;
  created_at:    string;
  last_used_at:  string | null;
  /** Pre-built example URL (`https://<slug>.solidpeer.io/v1/graphql`
   *  for chaingraph, `/` for BCHN/Fulcrum). The dashboard's copy
   *  buttons render this directly. */
  url:           string;
}

/** GET /billing/account/:id/endpoint-aliases — signed. Returns all
 *  aliases owned by the signing account, newest first. */
export function listEndpointAliases(identity: Identity): Promise<{ aliases: EndpointAliasDto[] }> {
  return call<{ aliases: EndpointAliasDto[] }>({
    method: 'GET',
    path:   `/billing/account/${identity.account_id}/endpoint-aliases`,
    identity,
  });
}

/** POST /billing/account/:id/endpoint-aliases — signed. Creates a
 *  new (token, system, network) binding; the server mints the slug
 *  randomly (~64 bits entropy from a wordlist). Customer can attach
 *  an optional display_label for in-dashboard naming. 409 when the
 *  (token, system, network) triple already has an alias. */
export function createEndpointAlias(identity: Identity, body: {
  token_id:       string;
  system:         string;
  network:        string;
  display_label?: string | null;
}): Promise<EndpointAliasDto> {
  return call<EndpointAliasDto>({
    method:   'POST',
    path:     `/billing/account/${identity.account_id}/endpoint-aliases`,
    body,
    identity,
  });
}

/** DELETE /billing/account/:id/endpoint-aliases/:slug — signed.
 *  Idempotent: 404 if the slug doesn't exist for this account. */
export function deleteEndpointAlias(identity: Identity, slug: string): Promise<{ deleted: true; slug: string }> {
  return call<{ deleted: true; slug: string }>({
    method:   'DELETE',
    path:     `/billing/account/${identity.account_id}/endpoint-aliases/${slug}`,
    identity,
  });
}
